import { useRouter } from 'next/router'

import { LOGIN_MODAL_TRIGGERED_PLACE_KEY, LoginModalTriggeredPlace, MODAL_HASHES } from '../utils/constants'
import useIsUrlHashIncluded from './useIsUrlHashIncluded'
import useUrls from './useUrls'
import { AnalyticsContext, AnalyticsLocation, LoginModalEvents, TrackingActions } from '../utils/types/analytics'
import useTrackingContext from './trackingContext/useTrackingContext'
import useCustomGlobalState from '../components/hooks/useCustomGlobalState'

const useLoginModal = () => {
  const [shouldShowModal, setShouldShowModal] = useCustomGlobalState('shouldShowLoginModal', false)

  const {
    LOGIN_URL_HASH,
    REGISTER_URL_HASH,
    INVITE_URL_HASH,
    LOGIN_PASSWORD_URL_HASH,
  } = MODAL_HASHES

  const isLoginUrlHashIncluded = useIsUrlHashIncluded([
    LOGIN_URL_HASH,
    REGISTER_URL_HASH,
    INVITE_URL_HASH,
    LOGIN_PASSWORD_URL_HASH,
  ])
  const { replaceT, pushT } = useUrls()
  const { asPath, pathname } = useRouter()
  const isOnOAuthPage = pathname === '/oauth/[action]/[provider]'

  const { dispatchTrackingContext } = useTrackingContext()

  const redirectToMyAccount = () => pushT('/my-account')

  const closeLoginModal = () => {
    setShouldShowModal(false)
  }

  // Used to push analytics location to tracking context on modal open and close clicks
  // Also used to empty analytics context on modal close cleanup function
  const setAnalyticsLocation = ({
    analyticsLocation,
    remove = false,
    urlHash,
  }: {
    analyticsLocation?: AnalyticsLocation | AnalyticsContext | ''
    remove?: boolean
    urlHash?: string
  }) => {
    const isLogin = urlHash?.includes(LOGIN_URL_HASH)
    const analyticsFunction = isLogin ? 'login_modal' : 'register_modal'

    // TODO: Remove type assertions, added to speed things up.
    // Linear card created to properly refactor: D1-1507
    const analyticsContext = (analyticsLocation
      ? `${analyticsLocation}_${analyticsFunction}`
      : `direct_${analyticsFunction}`) as AnalyticsContext

    Object.values(LoginModalEvents).forEach((event) => {
      dispatchTrackingContext({
        eventName: event,
        eventParameters: {
          context: analyticsContext || '',
        },
      },
      remove ? TrackingActions.Remove : TrackingActions.Add)
    })
  }

  const openModal = (urlHash: string, analyticsLocation?: AnalyticsLocation | AnalyticsContext | '') => {
    setShouldShowModal(true)

    const hashedUrl = `${asPath.split('#')[0]}${urlHash}`

    replaceT(
      hashedUrl,
      undefined,
      { shallow: true, scroll: false },
    )

    if (analyticsLocation) {
      setAnalyticsLocation({ analyticsLocation, urlHash })
    }
  }

  const redirectAfterLogin = () => {
    const triggeredPlace = sessionStorage.getItem(LOGIN_MODAL_TRIGGERED_PLACE_KEY)
    switch (triggeredPlace) {
      case LoginModalTriggeredPlace.productHeart:
        break

      case LoginModalTriggeredPlace.headerHeart:
        pushT('/my-products')
        break

      default:
        pushT('/my-account')
        break
    }

    sessionStorage.removeItem(LOGIN_MODAL_TRIGGERED_PLACE_KEY)
  }

  const openLoginModal = (
    analyticsLocation?: AnalyticsLocation | AnalyticsContext | '',
  ) => openModal(LOGIN_URL_HASH, analyticsLocation)

  const openRegisterModal = (
    analyticsLocation?: AnalyticsLocation,
  ) => openModal(REGISTER_URL_HASH, analyticsLocation)

  const openLoginPasswordModal = (
    analyticsLocation?: AnalyticsLocation | AnalyticsContext | '',
  ) => openModal(MODAL_HASHES.LOGIN_PASSWORD_URL_HASH, analyticsLocation)

  const isReadyToShowModal = !isOnOAuthPage

  const showLoginModal = (isLoginUrlHashIncluded && isReadyToShowModal)
   || (shouldShowModal && isReadyToShowModal)

  return {
    shouldShowLoginModal: showLoginModal,
    closeLoginModal,
    openLoginModal,
    openRegisterModal,
    openLoginPasswordModal,
    redirectToMyAccount,
    redirectAfterLogin,
    setAnalyticsLocation,
  }
}

export default useLoginModal
